/*
 * Data layer for the public site design.
 *
 * The design pages were written against their own fetch helpers. This module keeps the same
 * function names and record types, but every request goes through the project's existing
 * services and hooks (same endpoints, tenant header, auth, DEV proxy and normalisation), so the
 * API bindings are unchanged. Records are only reshaped into the fields the design reads.
 */
import { fetchPublicBlogs } from "@jambushrushti/services/publicBlogs";
import { fetchPublicEvents } from "@jambushrushti/services/publicEvents";
import { fetchPublicGallery } from "@jambushrushti/services/publicGallery";
import { fetchPublicGuruvani } from "@jambushrushti/services/publicGuruvani";
import { fetchPublicTelecasts } from "@jambushrushti/services/publicTelecasts";
import { fetchLibraryBooks } from "@jambushrushti/services/publicBooks";
import { fetchEmagazines } from "@jambushrushti/hooks/useEmagazines";
import { fetchPublicArticles } from "@jambushrushti/hooks/useContent";

/* ---------- shared helpers ---------- */

const API_BASE = import.meta.env.DEV
  ? "/jambushrusti-api"
  : (import.meta.env.VITE_API_BASE_URL || "https://api.jambushusti.universal-software.in/api").replace(/\/$/, "");
const TENANT_ID = import.meta.env.VITE_X_TENANTID || "6cf833d7-bf6f-4688-9136-5674d8053b85";
const API_ORIGIN = "https://api.jambushusti.universal-software.in";

/** CMS media URLs sometimes come back on http; upgrade them to https. */
export function mediaUrl(url?: string | null): string | null {
  if (!url || url === "/placeholder.svg") {return null;}
  return url.replace(/^http:\/\/api\.jambushusti\.universal-software\.in/, API_ORIGIN);
}

/** dd-mm-yyyy (CMS issue_date) or ISO, as a Date */
export function parseCmsDate(s?: string | null): Date | null {
  if (!s) {return null;}
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s);
  const d = m ? new Date(+m[3], +m[2] - 1, +m[1]) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** YouTube video id from any YouTube URL (watch, youtu.be, embed, live, shorts). */
export function youtubeId(url?: string | null): string | null {
  if (!url) {return null;}
  const m = /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|live\/|shorts\/|v\/))([\w-]{11})/.exec(url);
  return m ? m[1] : null;
}

const abortable = async <T>(signal: AbortSignal | undefined, p: Promise<T>): Promise<T> => {
  const v = await p;
  if (signal?.aborted) {throw new Error("aborted");}
  return v;
};

/* ---------- types read by the design ---------- */

export type BlogPost = {
  id: string;
  title: string;
  author: string;
  excerpt: string;
  cover_image: string | null;
  created_at: string;
};

export type Post = BlogPost & { category?: string; content?: string; updated_at?: string; is_published?: boolean };

export type CmsEvent = {
  id: string;
  title: string;
  start_date?: string;
  date?: string;
  start_time?: string;
  end_time?: string;
  location?: string;
};

export type CmsEventFull = CmsEvent & {
  description?: string;
  content?: string;
  end_date?: string;
  venue?: string;
  image?: string | null;
  cover_image?: string | null;
  banner_image?: string | null;
  category?: string;
  registration_url?: string | null;
  is_published?: boolean;
};

export type MagazineIssue = {
  id: string;
  title: string;
  volume?: string;
  year?: number;
  issue_date?: string;
  pdf_url?: string | null;
  cover_image?: string | null;
  description?: string;
  is_published?: boolean;
  created_at?: string;
};

export type LibraryRecord = {
  id: string;
  title: string;
  author?: string;
  category?: string;
  language?: string;
  year?: number | string;
  description?: string;
  excerpt?: string;
  content?: string;
  cover_image?: string | null;
  pdf_url?: string | null;
  file?: string | null;
  audio_url?: string | null;
  is_published?: boolean;
  created_at?: string;
};

export type GalleryRecord = {
  id: string;
  title?: string;
  caption?: string;
  description?: string;
  category?: string;
  media_type?: string;
  type?: string;
  image?: string | null;
  image_url?: string | null;
  file?: string | null;
  thumbnail?: string | null;
  video_url?: string | null;
  youtube_url?: string | null;
  is_published?: boolean;
  created_at?: string;
};

export type Telecast = {
  id: string;
  title: string;
  description?: string;
  location?: string;
  venue?: string;
  source?: string;
  category?: string;
  telecast_type?: string;
  is_mjrc?: boolean;
  stream_url?: string | null;
  youtube_url?: string | null;
  video_url?: string | null;
  live_url?: string | null;
  thumbnail?: string | null;
  cover_image?: string | null;
  is_live?: boolean;
  status?: string;
  start_time?: string;
  scheduled_at?: string;
  start_date?: string;
  date?: string;
  is_published?: boolean;
  created_at?: string;
};

/* ---------- Blog posts, articles & events ---------- */

const NOT_SPECIFIED = /^(not specified|author not specified|uncategorized|description not available\.)$/i;
const real = (v?: string | null) => (v && !NOT_SPECIFIED.test(v) ? v : undefined);

export const fetchAllPosts = (signal?: AbortSignal): Promise<Post[]> =>
  abortable(signal, fetchPublicBlogs(signal)).then((rows) =>
    rows.map((p) => ({
      id: p.id,
      title: p.title,
      author: p.author ?? "",
      excerpt: p.excerpt,
      content: p.content,
      cover_image: p.cover_image,
      created_at: p.created_at ?? "",
    })),
  );

export const fetchBlogPosts = fetchAllPosts;

/** Row of the Supabase `articles` table (see integrations/supabase/types.ts). */
type ArticleRow = {
  id: string;
  title: string;
  author: string | null;
  category: string | null;
  content: string | null;
  cover_image: string | null;
  excerpt: string | null;
  created_at: string;
  updated_at: string;
};

export const fetchAllArticles = (signal?: AbortSignal): Promise<Post[]> =>
  abortable(signal, fetchPublicArticles() as Promise<ArticleRow[]>).then((rows) =>
    rows.map((a) => ({
      id: a.id,
      title: a.title,
      author: a.author ?? "",
      excerpt: a.excerpt ?? "",
      content: a.content ?? "",
      category: a.category ?? undefined,
      cover_image: a.cover_image,
      created_at: a.created_at,
      updated_at: a.updated_at,
    })),
  );

export const fetchArticles = (signal?: AbortSignal): Promise<LibraryRecord[]> =>
  fetchAllArticles(signal).then((rows) =>
    rows.map((a) => ({ ...a, author: a.author || undefined, description: a.excerpt })),
  );

export const fetchAllEvents = (signal?: AbortSignal): Promise<CmsEventFull[]> =>
  abortable(signal, fetchPublicEvents(signal)).then((rows) =>
    rows.map((e) => ({
      id: e.id,
      title: e.title,
      start_date: e.event_date ?? undefined,
      location: e.location ?? undefined,
      description: e.description,
      image: e.image_url,
    })),
  );

export const fetchEvents = fetchAllEvents;

/* ---------- Library, Guruvani & eMagazine ---------- */

export const fetchBooks = (signal?: AbortSignal): Promise<LibraryRecord[]> =>
  abortable(signal, fetchLibraryBooks(signal)).then((rows) =>
    rows.map((b) => ({
      id: b.id,
      title: b.title,
      author: real(b.author),
      category: real(b.category),
      language: real(b.language),
      year: real(b.year),
      description: real(b.description),
      cover_image: mediaUrl(b.image),
      pdf_url: b.pdf_url,
      audio_url: b.audio_url,
    })),
  );

export const fetchGuruvani = (signal?: AbortSignal): Promise<LibraryRecord[]> =>
  abortable(signal, fetchPublicGuruvani(signal)).then((rows) =>
    rows.map((g) => ({
      id: g.id,
      title: g.title,
      category: g.category ?? undefined,
      content: g.content ?? undefined,
      description: g.source ?? undefined,
      cover_image: g.image_url ?? null,
      created_at: g.created_at,
    })),
  );

export const fetchMagazines = (signal?: AbortSignal): Promise<MagazineIssue[]> =>
  abortable(signal, fetchEmagazines()).then((rows) =>
    rows
      .filter((m) => m.is_published)
      .map((m) => ({
        id: m.id,
        title: m.title,
        volume: m.volume ?? undefined,
        year: m.year || undefined,
        issue_date: m.issue_date ?? undefined,
        pdf_url: m.pdf_url || null,
        cover_image: m.cover_image,
        description: m.description ?? undefined,
        created_at: m.created_at,
      }))
      .sort((a, b) => (parseCmsDate(b.issue_date)?.getTime() ?? 0) - (parseCmsDate(a.issue_date)?.getTime() ?? 0)),
  );

/* ---------- Gallery & Live Telecast ---------- */

export const fetchMediaGallery = (signal?: AbortSignal): Promise<GalleryRecord[]> =>
  abortable(signal, fetchPublicGallery(signal)).then((rows) =>
    rows.map((p) => ({
      id: p.id,
      title: p.title,
      description: p.description ?? undefined,
      category: p.category ?? p.category_division,
      media_type: "image",
      image: p.image_url,
    })),
  );

export const fetchTelecasts = (signal?: AbortSignal): Promise<Telecast[]> =>
  abortable(signal, fetchPublicTelecasts(signal)).then((rows) =>
    rows.map((t) => ({
      id: t.id,
      title: t.title,
      description: t.description ?? undefined,
      is_mjrc: t.source_type === "from_mjrc",
      source: t.source_type === "from_mjrc" ? "MJRC" : "Outside",
      stream_url: t.stream_url,
      thumbnail: t.thumbnail_url,
      is_live: t.is_live,
      start_date: t.event_date ?? undefined,
      created_at: t.created_at ?? undefined,
    })),
  );

/* ---------- Contact & visitor feedback ---------- */

export type SubmitResult = { ok: true } | { ok: false; message: string; fields: Record<string, string> };

/** POSTs a public CMS form (same endpoints the old Contact and Feedback pages used). */
async function submit(path: string, body: Record<string, unknown>): Promise<SubmitResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/jambushrusti/cms/${path}/`, {
      method: "POST",
      headers: { "X-Tenant-ID": TENANT_ID, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, message: "We could not reach the server. Please check your connection and try again.", fields: {} };
  }
  if (res.ok) {return { ok: true };}
  const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  const fields: Record<string, string> = {};
  const source = (data?.error as { details?: unknown } | undefined)?.details ?? data;
  if (source && typeof source === "object") {
    for (const [k, v] of Object.entries(source as Record<string, unknown>)) {
      if (k in body) {fields[k] = Array.isArray(v) ? String(v[0]) : String(v);}
    }
  }
  const message =
    (data?.error as { message?: string } | undefined)?.message ??
    (data?.detail as string | undefined) ??
    `The message could not be sent (HTTP ${res.status}).`;
  return { ok: false, message, fields };
}

export type ContactMessage = { name: string; email: string; inquiry_type: string; subject: string; message: string };
export type VisitorFeedback = { name: string; email: string; phone_number: string; feedback_type: string; rating: number; message: string };

export const sendContact = (m: ContactMessage) => submit("contact-us", m);
export const sendFeedback = (f: VisitorFeedback) => submit("visitor-feedback", f);
